import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import { Field, DataTable } from "@/pages/FridgeLogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil, ShieldCheck, ShieldAlert, GraduationCap, KeyRound, Lock, Eye } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "",
  email: "",
  date_of_birth: "",
  address: "",
  allergies: "",
  emergency_contact_name: "",
  emergency_contact_relationship: "",
  emergency_contact_phone: "",
  critical_medical_info: "",
};

function certStatus(expiry) {
  if (!expiry) return "no_expiry";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const ex = new Date(expiry); ex.setHours(0, 0, 0, 0);
  const days = Math.round((ex - today) / 86400000);
  if (days < 0) return "expired";
  if (days <= 30) return "renew";
  return "active";
}

function certTone(status) {
  if (status === "expired") return "alert";
  if (status === "renew") return "warning";
  if (status === "active") return "success";
  if (status === "no_expiry") return "brand";
  return "neutral";
}

export default function Staff() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [training, setTraining] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [accessDialog, setAccessDialog] = useState(null); // staff record or null

  const load = async () => setItems((await api.get("/staff")).data);
  const loadTraining = async () => {
    try { setTraining((await api.get("/training")).data); } catch { setTraining([]); }
  };
  useEffect(() => { load(); loadTraining(); }, []);

  const isOwner = user?.role === "owner";

  const isSelfRecord = (record) =>
    !!user?.email && !!record?.email && user.email.toLowerCase() === record.email.toLowerCase();

  // Locked = the viewer can see this record but cannot edit any field on it
  // (i.e. record was granted to them but isn't their own and they aren't owner).
  const isLockedView = (record) => !isOwner && !isSelfRecord(record);

  const canSeeSensitive = (record) => isOwner || isSelfRecord(record);

  const openCreate = () => {
    if (!isOwner) { toast.error("Only owners can add staff records"); return; }
    setEditingId(null); setEditingRecord(null); setForm(empty); setOpen(true);
  };
  const openEdit = (r) => {
    setEditingId(r.id);
    setEditingRecord(r);
    setForm({
      name: r.name || "",
      email: r.email || "",
      date_of_birth: r.date_of_birth || "",
      address: r.address || "",
      allergies: r.allergies || "",
      emergency_contact_name: r.emergency_contact_name || "",
      emergency_contact_relationship: r.emergency_contact_relationship || "",
      emergency_contact_phone: r.emergency_contact_phone || "",
      critical_medical_info: canSeeSensitive(r) ? (r.critical_medical_info || "") : "",
    });
    setOpen(true);
  };

  const certsFor = (staffName) =>
    training.filter((t) => (t.staff_name || "").toLowerCase() === (staffName || "").toLowerCase());

  const locked = editingRecord ? isLockedView(editingRecord) : false;
  const selfEditing = editingRecord && !isOwner && isSelfRecord(editingRecord);
  // Fields the staff member can edit on their own record:
  const SELF_EDITABLE = useMemo(() => new Set([
    "address", "allergies",
    "emergency_contact_name", "emergency_contact_relationship", "emergency_contact_phone",
    "critical_medical_info",
  ]), []);
  const fieldDisabled = (key) => {
    if (locked) return true;            // read-only view of someone else's record
    if (isOwner) return false;          // owner can edit anything
    if (selfEditing) return !SELF_EDITABLE.has(key); // self: only the whitelisted fields
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (locked) return;
    setBusy(true);
    try {
      const body = { ...form, date_of_birth: form.date_of_birth || null };
      if (editingId) {
        await api.put(`/staff/${editingId}`, body);
        toast.success("Staff record updated");
      } else {
        await api.post("/staff", body);
        toast.success("Staff member added");
      }
      setOpen(false);
      setEditingId(null);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!isOwner) { toast.error("Only owners can delete staff records"); return; }
    if (!window.confirm("Delete this staff record?")) return;
    await api.delete(`/staff/${id}`);
    toast.success("Staff record removed");
    load();
  };

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-[1400px]" data-testid="staff-page">
      <PageHeader
        eyebrow="People"
        title="Staff records"
        description={isOwner
          ? "Contact details, emergency numbers and dietary needs — and who on the team can see each record."
          : "Your record + any records the owner has shared with you."}
        actions={
          isOwner ? (
            <Button data-testid="staff-add-button" onClick={openCreate} className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px] h-10 px-5">
              <Plus className="w-4 h-4 mr-2" /> New staff record
            </Button>
          ) : null
        }
      />

      <div className="bg-[#fdf3e1] border border-[#d9953d]/30 px-4 py-3 mb-6 text-xs text-[#57534e] flex items-start gap-3" data-testid="staff-privacy-notice">
        <ShieldCheck className="w-4 h-4 text-[#d9953d] mt-0.5 shrink-0" />
        <div>
          {isOwner ? (
            <>
              <span className="font-medium text-[#1c1917]">Privacy</span> — Each staff record is private to the owner and the staff member themselves. Use <span className="font-medium">Manage access</span> on any row to grant additional viewers (e.g. shift managers).
            </>
          ) : (
            <>
              <span className="font-medium text-[#1c1917]">Your records</span> — You see your own record plus anything the owner has shared with you. You can update your address, emergency contact and medical info on your own record; the owner manages everything else.
            </>
          )}
        </div>
      </div>

      <DataTable
        empty={isOwner ? "No staff records yet. Add your team to start tracking allergies and emergency contacts." : "Nothing shared with you yet. Ask the owner to grant access if needed."}
        head={["Name", "Email", "Date of birth", "Allergies", "Certifications", "Medical", ""]}
        rows={items}
        testid="staff-table"
        render={(s, idx) => {
          const sensitive = canSeeSensitive(s);
          const certs = certsFor(s.name);
          const expiredCount = certs.filter((c) => certStatus(c.expiry_date) === "expired").length;
          const renewCount = certs.filter((c) => certStatus(c.expiry_date) === "renew").length;
          const mine = isSelfRecord(s);
          return (
            <tr key={s.id} data-testid={`staff-row-${idx}`} className="border-t border-[#e5e2d9] hover:bg-[#efece6]/40 align-top">
              <td className="px-4 py-3 text-sm">
                <div className="font-medium inline-flex items-center gap-2">
                  {s.name}
                  {mine && (
                    <span data-testid={`staff-self-badge-${idx}`} className="text-[9px] uppercase tracking-[0.18em] text-[#4e7b5b] border border-[#4e7b5b]/40 px-1.5 py-0.5">you</span>
                  )}
                </div>
                {s.address && <div className="text-xs text-[#8a8476] mt-0.5 line-clamp-1">{s.address}</div>}
              </td>
              <td className="px-4 py-3 text-sm text-[#57534e]">{s.email || "—"}</td>
              <td className="px-4 py-3 text-sm whitespace-nowrap">{s.date_of_birth || "—"}</td>
              <td className="px-4 py-3 text-sm text-[#57534e] max-w-[180px] truncate">{s.allergies || "—"}</td>
              <td className="px-4 py-3" data-testid={`staff-certs-${idx}`}>
                {certs.length === 0 ? (
                  <span className="text-xs text-[#8a8476]">—</span>
                ) : (
                  <div className="inline-flex items-center gap-2 text-xs">
                    <GraduationCap className="w-3.5 h-3.5 text-[#4e7b5b]" />
                    <span className="font-mono text-[#1c1917]">{certs.length}</span>
                    {expiredCount > 0 && (
                      <StatusPill tone="alert"><span data-testid={`staff-certs-expired-${idx}`}>{expiredCount} expired</span></StatusPill>
                    )}
                    {renewCount > 0 && (
                      <StatusPill tone="warning"><span data-testid={`staff-certs-renew-${idx}`}>{renewCount} renew</span></StatusPill>
                    )}
                  </div>
                )}
              </td>
              <td className="px-4 py-3 text-sm">
                {sensitive ? (
                  s.critical_medical_info ? (
                    <span className="text-[#57534e] inline-flex items-start gap-1.5" data-testid={`staff-medical-${idx}`}>
                      <ShieldCheck className="w-3.5 h-3.5 mt-[3px] text-[#4e7b5b] shrink-0" />
                      <span className="max-w-[200px] truncate">{s.critical_medical_info}</span>
                    </span>
                  ) : <span className="text-[#8a8476]">—</span>
                ) : (
                  <span className="text-[#8a8476] inline-flex items-center gap-1.5" data-testid={`staff-medical-restricted-${idx}`}>
                    <ShieldAlert className="w-3.5 h-3.5" /> [restricted]
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="inline-flex items-center gap-3">
                  {isOwner && (
                    <button
                      data-testid={`staff-access-${idx}`}
                      onClick={() => setAccessDialog(s)}
                      className="text-[#8a8476] hover:text-[#1b3b36]"
                      aria-label="Manage access"
                      title="Manage access"
                    >
                      <KeyRound className="w-4 h-4" />
                    </button>
                  )}
                  <button data-testid={`staff-edit-${idx}`} onClick={() => openEdit(s)} className="text-[#8a8476] hover:text-[#1b3b36]" aria-label={isLockedView(s) ? "View" : "Edit"}>
                    {isLockedView(s) ? <Eye className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                  </button>
                  {isOwner && (
                    <button data-testid={`staff-delete-${idx}`} onClick={() => del(s.id)} className="text-[#8a8476] hover:text-[#d94f3d]" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </td>
            </tr>
          );
        }}
      />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(empty); setEditingRecord(null); } }}>
        <DialogContent className="bg-white max-w-xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {!editingId ? "New staff record" : locked ? "View staff record" : "Edit staff record"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Contact details, emergency contacts and sensitive medical info.
            </DialogDescription>
          </DialogHeader>

          {selfEditing && (
            <div className="bg-[#e5efe8] border border-[#1b3b36]/15 px-3 py-2 text-[11px] text-[#1b3b36] flex items-start gap-2" data-testid="staff-self-banner">
              <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>This is your record. You can update address, emergency contact and medical info. Other fields are owner-managed.</span>
            </div>
          )}
          {locked && (
            <div className="bg-[#efece6] border border-[#e5e2d9] px-3 py-2 text-[11px] text-[#57534e] flex items-start gap-2" data-testid="staff-locked-banner">
              <Eye className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>Read-only view. The owner has shared this record with you but only they can change it.</span>
            </div>
          )}

          <form className="space-y-4" onSubmit={submit} data-testid="staff-form">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Full name">
                <Input required disabled={fieldDisabled("name")} data-testid="staff-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="bg-white rounded-[4px] disabled:bg-[#efece6] disabled:cursor-not-allowed" />
              </Field>
              <Field label="Email">
                <Input type="email" disabled={fieldDisabled("email")} data-testid="staff-email-input" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="bg-white rounded-[4px] disabled:bg-[#efece6] disabled:cursor-not-allowed" />
              </Field>
            </div>
            <Field label="Date of birth">
              <Input type="date" disabled={fieldDisabled("date_of_birth")} data-testid="staff-dob-input" value={form.date_of_birth} onChange={(e) => setForm((f) => ({ ...f, date_of_birth: e.target.value }))} className="bg-white rounded-[4px] disabled:bg-[#efece6] disabled:cursor-not-allowed" />
            </Field>
            <Field label="Address">
              <Textarea disabled={fieldDisabled("address")} data-testid="staff-address-input" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} className="bg-white rounded-[4px] disabled:bg-[#efece6] disabled:cursor-not-allowed" rows={2} />
            </Field>
            <Field label="Allergies">
              <Input disabled={fieldDisabled("allergies")} data-testid="staff-allergies-input" value={form.allergies} onChange={(e) => setForm((f) => ({ ...f, allergies: e.target.value }))} className="bg-white rounded-[4px] disabled:bg-[#efece6] disabled:cursor-not-allowed" placeholder="e.g. Peanuts, shellfish" />
            </Field>
            <Field label="Emergency contact">
              <div className="grid grid-cols-2 gap-2">
                <Input disabled={fieldDisabled("emergency_contact_name")} data-testid="staff-emergency-name-input" value={form.emergency_contact_name} onChange={(e) => setForm((f) => ({ ...f, emergency_contact_name: e.target.value }))} className="bg-white rounded-[4px] disabled:bg-[#efece6] disabled:cursor-not-allowed" placeholder="Full name" />
                <Input disabled={fieldDisabled("emergency_contact_relationship")} data-testid="staff-emergency-relationship-input" value={form.emergency_contact_relationship} onChange={(e) => setForm((f) => ({ ...f, emergency_contact_relationship: e.target.value }))} className="bg-white rounded-[4px] disabled:bg-[#efece6] disabled:cursor-not-allowed" placeholder="Relationship (e.g. Partner)" />
              </div>
              <Input disabled={fieldDisabled("emergency_contact_phone")} data-testid="staff-emergency-phone-input" type="tel" value={form.emergency_contact_phone} onChange={(e) => setForm((f) => ({ ...f, emergency_contact_phone: e.target.value }))} className="bg-white rounded-[4px] mt-2 font-mono disabled:bg-[#efece6] disabled:cursor-not-allowed" placeholder="Phone number" />
            </Field>

            {editingRecord && (
              <div className="border-t border-[#e5e2d9] pt-4" data-testid="staff-certs-inline">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476] mb-2 flex items-center gap-2">
                  <GraduationCap className="w-3.5 h-3.5" />
                  Certifications on file <span className="text-[10px] normal-case tracking-normal text-[#8a8476]">(training is owner-managed)</span>
                </div>
                {certsFor(editingRecord.name).length === 0 ? (
                  <div className="text-xs text-[#8a8476] italic">
                    No training records yet for {editingRecord.name}.
                  </div>
                ) : (
                  <ul className="space-y-1.5">
                    {certsFor(editingRecord.name).map((c, i) => {
                      const st = certStatus(c.expiry_date);
                      return (
                        <li key={c.id} data-testid={`staff-cert-row-${i}`} className="flex items-center justify-between text-sm border-b border-[#efece6] last:border-0 pb-1.5">
                          <div className="min-w-0">
                            <div className="truncate">{c.course_name}</div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8476]">
                              Completed {c.completion_date} {c.expiry_date && `• expires ${c.expiry_date}`}
                            </div>
                          </div>
                          <StatusPill tone={certTone(st)}>{st === "no_expiry" ? "no expiry" : st}</StatusPill>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            <div className="border-t border-[#e5e2d9] pt-4">
              <div className="flex items-start gap-2 mb-2">
                <ShieldAlert className="w-4 h-4 mt-0.5 text-[#d94f3d] shrink-0" />
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#d94f3d] font-medium">Sensitive — owner & self only</div>
              </div>
              <Field label="Critical medical information">
                <Textarea
                  disabled={fieldDisabled("critical_medical_info")}
                  data-testid="staff-medical-input"
                  value={form.critical_medical_info}
                  onChange={(e) => setForm((f) => ({ ...f, critical_medical_info: e.target.value }))}
                  className="bg-white rounded-[4px] disabled:bg-[#efece6] disabled:cursor-not-allowed"
                  rows={3}
                  placeholder="Conditions, medications, anything paramedics need to know in an emergency."
                />
              </Field>
            </div>

            {!locked && (
              <DialogFooter>
                <Button type="submit" disabled={busy} data-testid="staff-submit-button" className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px]">
                  {busy ? "Saving…" : editingId ? "Save changes" : "Save record"}
                </Button>
              </DialogFooter>
            )}
          </form>
        </DialogContent>
      </Dialog>

      <AccessDialog
        record={accessDialog}
        onClose={() => setAccessDialog(null)}
      />
    </div>
  );
}

function AccessDialog({ record, onClose }) {
  const [grants, setGrants] = useState([]);
  const [users, setUsers] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!record) { setGrants([]); setUsers([]); setPick(""); return; }
    (async () => {
      try {
        const [gRes, uRes] = await Promise.all([
          api.get(`/staff/${record.id}/permissions`),
          api.get("/users"),
        ]);
        setGrants(gRes.data);
        setUsers(uRes.data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't load access");
      }
    })();
  }, [record]);

  const grantedIds = useMemo(() => new Set(grants.map((g) => g.user_id)), [grants]);
  // Hide users who already have the grant + the staff member themselves (they auto-see their own).
  const selectable = useMemo(() => users.filter((u) => {
    if (grantedIds.has(u.id)) return false;
    if (u.role === "owner") return false; // owner already sees all
    const sameEmail = (u.email || "").toLowerCase() === (record?.email || "").toLowerCase();
    if (sameEmail && record?.email) return false;
    return true;
  }), [users, grantedIds, record]);

  const grant = async () => {
    if (!pick || !record) return;
    setBusy(true);
    try {
      await api.post(`/staff/${record.id}/permissions`, { user_id: pick });
      const { data } = await api.get(`/staff/${record.id}/permissions`);
      setGrants(data);
      setPick("");
      toast.success("Access granted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const revoke = async (userId) => {
    if (!record) return;
    if (!window.confirm("Revoke access to this record?")) return;
    try {
      await api.delete(`/staff/${record.id}/permissions/${userId}`);
      setGrants((g) => g.filter((x) => x.user_id !== userId));
      toast.success("Access revoked");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <Dialog open={!!record} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-white max-w-lg" data-testid="staff-access-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Manage access</DialogTitle>
          <DialogDescription className="text-sm text-[#57534e]">
            {record ? <>Choose who else can view <span className="font-medium text-[#1c1917]">{record.name}</span>&apos;s record. Granted users get read-only access — they can&apos;t change it.</> : null}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">Currently with access</div>
          <div className="border border-[#e5e2d9] divide-y divide-[#efece6]" data-testid="staff-access-list">
            {grants.length === 0 && (
              <div className="px-4 py-4 text-xs text-[#8a8476] italic">Only the owner and the staff member themselves can see this record.</div>
            )}
            {grants.map((g, idx) => (
              <div key={g.user_id} data-testid={`staff-access-row-${idx}`} className="px-4 py-2.5 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{g.name || g.email}</div>
                  <div className="text-[10px] text-[#8a8476] uppercase tracking-[0.18em]">{g.email} · {g.role}</div>
                </div>
                <button
                  type="button"
                  data-testid={`staff-access-revoke-${idx}`}
                  onClick={() => revoke(g.user_id)}
                  className="text-[#8a8476] hover:text-[#d94f3d]"
                  aria-label="Revoke"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="border-t border-[#e5e2d9] pt-4 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">Grant access to…</div>
            <div className="flex items-center gap-2">
              <Select value={pick} onValueChange={setPick}>
                <SelectTrigger data-testid="staff-access-user-select" className="bg-white rounded-[4px] flex-1">
                  <SelectValue placeholder={selectable.length ? "Pick a team member" : "No more users to grant"} />
                </SelectTrigger>
                <SelectContent>
                  {selectable.map((u) => (
                    <SelectItem key={u.id} value={u.id} data-testid={`staff-access-user-${u.id}`}>
                      <span>{u.name || u.email}</span>
                      <span className="text-[#8a8476] ml-2 text-[11px]">{u.email} · {u.role}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                data-testid="staff-access-grant-button"
                onClick={grant}
                disabled={!pick || busy}
                className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px]"
              >
                Grant
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
